import { useMutation, useQuery } from '@tanstack/react-query'
import type {
  CebiGlassInfoDto,
  GetCebiGlassRequest,
  GetMakersRequest,
  GetModelsBaseRequest,
  GetModelsRequest,
  GetModelsResponse,
  VehiclesIdentificationDto,
  VehicleValuationDto,
  VinOnlineVehicleDto,
} from 'BrokerTsLib/src/dtos'
import type { VehicleValuationModel } from 'BrokerTsLib/src/dtos'
import type { GenericQueryOptions } from 'brokertslib/src/hooks'
import { longStaleTime } from 'brokertslib/src/hooks'

import apiClient from 'BrokerTsLib/src/Utils/apiClient'
import { useUrl } from '.'
import { Apis } from '../appSettings/AppSettings'

export const useFindVehicle = () => {
  const url = useUrl(Apis.vinOnline)

  return useMutation({
    mutationFn: (data: VehicleIdentificationPropsDto) =>
      apiClient.post<VinOnlineVehicleDto>(`${url}/FindVehicle`, data)
  })
}

export const useVehicleIdentification = () => {
  const url = useUrl(Apis.vinOnline)

  return useMutation({
    mutationFn: (data: VehicleIdentificationPropsDto) =>
      apiClient.post<VehiclesIdentificationDto>(`${url}/VehicleIdentification`, data)
  })
}

export const useVehicleValuation = () => {
  const url = useUrl(Apis.vinOnline)

  return useMutation({
    mutationFn: (data: VehicleValuationModel) =>
      apiClient.post<VehicleValuationDto>(`${url}/VehicleValuation`, data)
  })
}

export type GetVehicleAveragePriceRequest = {
  vin?: string | null
  mileage?: number | null
  registrationDate?: Date | string | null
}

/** Průměrovaná (obvyklá) cena vozidla – ocenění bez výběru stupně výbavy. */
export const useGetVehicleAveragePrice = () => {
  const url = useUrl(Apis.vinOnline)

  return useMutation({
    mutationFn: (data: GetVehicleAveragePriceRequest) =>
      apiClient.post<VehicleValuationDto>(`${url}/GetVehicleAveragePrice`, data)
  })
}

export const useCebiGlass = () => {
  const url = useUrl(Apis.vinOnline)

  return useMutation({
    mutationFn: (data: GetCebiGlassRequest) =>
      apiClient.post<CebiGlassInfoDto>(`${url}/CebiGlass`, data)
  })
}

export type VehicleIdentificationPropsDto = {
  paramsId?: string | null
  spz?: string | null
  vin?: string | null
  techCertNumber?: string | null
}

export const useGetMakers = (request: GetMakersRequest) => {
  const url = useUrl(Apis.vinOnline)

  return useQuery({
    queryKey: ['vinOnline-useGetVehicleMakers', request.categoryId, request.fuelId],
    queryFn: async () => {
      if (!request.categoryId)
        return []

      const response = await apiClient.post<string[]>(`${url}/GetMakers`, request)

      return response.data
    },
    staleTime: longStaleTime
  })
}

export const useGetModelsBase = (request: GetModelsBaseRequest) => {
  const url = useUrl(Apis.vinOnline)

  return useQuery({
    queryKey: ['vinOnline-useGetModelsBase', request.categoryId, request.fuelId, request.maker],
    queryFn: async () => {
      if (!request.categoryId && !request.maker)
        return []

      const response = await apiClient.post<string[]>(`${url}/GetModelsBase`, request)

      return response.data
    },
    staleTime: longStaleTime,
  })
}


export const useGetModels = (request: GetModelsRequest) => {
  const url = useUrl(Apis.vinOnline)

  // prazdny zakladni model = zadne filtrovani (backend ma stejnou logiku pro null i ''),
  // normalizace zajisti shodny query key, takze se dotaz bez modelBase a s prazdnym modelBase nevola 2x
  const modelBase = request.modelBase || undefined

  return useQuery({
    queryKey: [
      'vinOnline-useGetModels',
      request.categoryId,
      request.fuelId,
      request.maker,
      modelBase,
    ],
    queryFn: async () => {
      if (!request.categoryId && !request.maker)
        return []

      const response = await apiClient.post<GetModelsResponse[]>(`${url}/GetModels`, { ...request, modelBase })

      return response.data
    },
    staleTime: longStaleTime
  })
}

export const useGetAllMakers = (
  options?: GenericQueryOptions<string[]>,
) => {
  const url = useUrl(Apis.vinOnline)

  return useQuery({
    queryKey: ['vinOnline-useGetAllVehicleMakers'],
    queryFn: () => apiClient.post<string[]>(`${url}/GetAllMakers`),
    staleTime: longStaleTime,
    ...options
  })
}